import React, { useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Paper,
  LinearProgress,
  Alert,
  Button,
  Chip,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  MoreVert as MoreIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import { BRAND_COLORS, alpha } from '../styles/theme';
import {
  FileGridContainer,
  FileCard,
  FileHeader,
  FileMetadata,
  MetadataRow,
  FileIcon,
} from './file-browser/styles';
import { useDocumentsQuery, type Document } from './Documents.queries';
import { useDeleteDocument, useDownloadDocument } from './Documents.mutations';
import { formatFileSize, formatDate } from './file-browser/utils';
import { getFileIconClass, getFileIconText } from './file-browser/utils';
import DocumentUploadDialog from './DocumentUploadDialog';
import { useErrorHandler } from '../hooks/useErrorHandler';
import { useTranslation } from '../i18n';

interface DocumentsViewProps {
  onBack: () => void;
}

const DocumentsView: React.FC<DocumentsViewProps> = ({ onBack }) => {
  const { t } = useTranslation();
  const { showSuccess } = useErrorHandler();
  const [uploadDialogOpen, setUploadDialogOpen] = useState<boolean>(false);
  const [contextMenuAnchor, setContextMenuAnchor] = useState<HTMLElement | null>(null);
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);

  const documentsQuery = useDocumentsQuery();
  const documents = documentsQuery.data ?? [];
  const downloadDocument = useDownloadDocument();
  const deleteDocument = useDeleteDocument();

  const handleDownload = async (document: Document) => {
    try {
      const downloadUrl = await downloadDocument.mutateAsync(document.id);
      window.open(downloadUrl, '_blank');
      showSuccess(t('documents.downloadedDocument', { title: document.title }));
    } catch {
      // Error toast is raised by the global mutation handler (lib/queryClient).
    }
  };

  const handleDelete = async (document: Document) => {
    if (!window.confirm(t('documents.confirmDelete', { title: document.title }))) {
      return;
    }

    try {
      await deleteDocument.mutateAsync(document.id);
      showSuccess(t('documents.deleteSuccess', { title: document.title }));
      // The list refreshes automatically via query invalidation in the mutation.
    } catch {
      // Error toast is raised by the global mutation handler (lib/queryClient).
    }
  };

  const handleContextMenu = (event: React.MouseEvent<HTMLElement>, document: Document) => {
    event.preventDefault();
    event.stopPropagation();
    setContextMenuAnchor(event.currentTarget);
    setSelectedDocument(document);
  };

  const handleCloseContextMenu = () => {
    setContextMenuAnchor(null);
    setSelectedDocument(null);
  };

  const handleUploadSuccess = () => {
    setUploadDialogOpen(false);
    // The list refreshes automatically via query invalidation in the mutation.
  };

  const getDocumentTypeColor = (type: string) => {
    switch (type) {
      case 'insurance':
        return BRAND_COLORS.forestGreen.main;
      case 'permit':
        return BRAND_COLORS.yellow.main;
      default:
        return '#757575'; // Material UI grey
    }
  };

  const getDocumentTypeLabel = (type: string) => {
    switch (type) {
      case 'insurance':
        return t('documents.typeInsurance');
      case 'permit':
        return t('documents.typePermit');
      default:
        return t('documents.typeOther');
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button onClick={onBack} variant="outlined" size="small">
            {t('documents.backToFiles')}
          </Button>
          <Typography
            variant="h5"
            sx={{ color: BRAND_COLORS.forestGreen.main, fontWeight: 'bold' }}
          >
            {t('documents.title')}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setUploadDialogOpen(true)}
          sx={{
            backgroundColor: BRAND_COLORS.forestGreen.main,
            '&:hover': {
              backgroundColor: alpha(BRAND_COLORS.forestGreen.main, 0.8),
            },
          }}
        >
          {t('documents.uploadDocument')}
        </Button>
      </Box>

      {documentsQuery.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t('documents.failedToLoad')}
        </Alert>
      )}

      {documentsQuery.isFetching && <LinearProgress sx={{ mb: 2 }} />}

      {/* Documents Grid */}
      {!documentsQuery.isFetching && documents.length === 0 ? (
        <Paper
          sx={{
            p: 4,
            textAlign: 'center',
            backgroundColor: alpha(BRAND_COLORS.forestGreen.main, 0.02),
          }}
        >
          <DescriptionIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography color="textSecondary" variant="h6" gutterBottom>
            {t('documents.noDocuments')}
          </Typography>
          <Typography color="textSecondary" sx={{ mb: 2 }}>
            {t('documents.uploadFirstDocument')}
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setUploadDialogOpen(true)}
            sx={{
              backgroundColor: BRAND_COLORS.forestGreen.main,
              '&:hover': {
                backgroundColor: alpha(BRAND_COLORS.forestGreen.main, 0.8),
              },
            }}
          >
            {t('documents.uploadDocument')}
          </Button>
        </Paper>
      ) : (
        <FileGridContainer>
          {documents.map((doc) => (
            <FileCard
              key={doc.id}
              onClick={() => handleDownload(doc)}
              onContextMenu={(e: React.MouseEvent<HTMLDivElement>) => handleContextMenu(e, doc)}
            >
              <FileHeader>
                <FileIcon className={getFileIconClass(doc.original_filename)}>
                  {getFileIconText(doc.original_filename)}
                </FileIcon>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 'bold',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {doc.title}
                  </Typography>
                  {doc.description && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: 'block',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        mt: 0.5,
                      }}
                    >
                      {doc.description}
                    </Typography>
                  )}
                </Box>
              </FileHeader>

              <FileMetadata>
                <MetadataRow>
                  <Chip
                    label={getDocumentTypeLabel(doc.document_type)}
                    size="small"
                    sx={{
                      backgroundColor: alpha(getDocumentTypeColor(doc.document_type), 0.1),
                      color: getDocumentTypeColor(doc.document_type),
                      height: '20px',
                      fontSize: '0.7rem',
                    }}
                  />
                  {doc.validity_end_date && (
                    <Typography variant="caption" color="text.secondary">
                      {t('documents.validUntil')} {formatDate(doc.validity_end_date)}
                    </Typography>
                  )}
                </MetadataRow>
                <MetadataRow>
                  <span>{formatDate(doc.created_at)}</span>
                  {doc.file_size && <span>{formatFileSize(doc.file_size)}</span>}
                </MetadataRow>
              </FileMetadata>

              {/* Context Menu Button */}
              <Box sx={{ position: 'absolute', top: 2, right: 2, opacity: 0.6 }}>
                <Tooltip title={t('documents.moreActions')}>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleContextMenu(e, doc);
                    }}
                    sx={{
                      padding: '1px',
                      width: '16px',
                      height: '16px',
                      '&:hover': {
                        backgroundColor: alpha(BRAND_COLORS.forestGreen.main, 0.1),
                        opacity: 1,
                      },
                    }}
                  >
                    <MoreIcon sx={{ fontSize: '12px' }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </FileCard>
          ))}
        </FileGridContainer>
      )}

      {/* Context Menu */}
      <Menu
        anchorEl={contextMenuAnchor}
        open={Boolean(contextMenuAnchor)}
        onClose={handleCloseContextMenu}
      >
        {selectedDocument && (
          <>
            <MenuItem
              onClick={() => {
                handleDownload(selectedDocument);
                handleCloseContextMenu();
              }}
            >
              <DownloadIcon sx={{ mr: 1 }} />
              {t('documents.download')}
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleDelete(selectedDocument);
                handleCloseContextMenu();
              }}
              sx={{ color: 'error.main' }}
            >
              <DeleteIcon sx={{ mr: 1 }} />
              {t('documents.delete')}
            </MenuItem>
          </>
        )}
      </Menu>

      {/* Upload Dialog */}
      <DocumentUploadDialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        onSuccess={handleUploadSuccess}
      />
    </Box>
  );
};

export default DocumentsView;
